import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import {
  createAdminBookingRequest,
  getAdminBookingDays,
  getAdminBookings,
  getAdminCustomers,
  scheduleBooking,
  submitBookingRequest,
  updateBookingStatus,
  type AdminBooking,
  type BookingCustomer,
  type BookingDay,
  type BookingStatus,
} from '../../lib/booking-api';
import { getAdminStaff, getStaffTimeOff, type StaffMember, type StaffTimeOff } from '../../lib/admin-staff-api';
import { getPublishedServices, type PublicService } from '../../lib/content-api';

const formatDate = (value: string) => new Intl.DateTimeFormat('en-NG', {
  weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
}).format(new Date(`${value}T00:00:00Z`));

const formatTime = (value: string | null) => value
  ? new Intl.DateTimeFormat('en-NG', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }).format(new Date(`1970-01-01T${value}Z`))
  : 'Time pending';

const timeOptions = Array.from({ length: 10 }, (_, index) => `${String(index + 9).padStart(2, '0')}:00`);
const activeStatuses: BookingStatus[] = ['pending_approval', 'approved', 'awaiting_payment', 'payment_pending', 'confirmed', 'arrived'];
const toMinutes = (value: string | null) => {
  if (!value) return null;
  const [hours, minutes] = value.slice(0, 5).split(':').map(Number);
  return hours * 60 + minutes;
};

export default function AdminBookings() {
  const [params, setParams] = useSearchParams();
  const [bookings, setBookings] = useState<AdminBooking[]>([]);
  const [customers, setCustomers] = useState<BookingCustomer[]>([]);
  const [services, setServices] = useState<PublicService[]>([]);
  const [days, setDays] = useState<BookingDay[]>([]);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [timeOff, setTimeOff] = useState<StaffTimeOff[]>([]);
  const [selectedId, setSelectedId] = useState(params.get('booking') || '');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [showCreate, setShowCreate] = useState(params.get('create') === '1');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [scheduleStaffId, setScheduleStaffId] = useState('');
  const [createDate, setCreateDate] = useState('');
  const [createTime, setCreateTime] = useState('09:00');
  const [createStaffId, setCreateStaffId] = useState('');

  const customerParam = params.get('customer');
  const prefillCustomer = customers.find((customer) => customer.id === customerParam) ?? null;

  async function refresh() {
    const from = new Date().toISOString().slice(0, 10);
    const to = new Date(Date.now() + 45 * 86400000).toISOString().slice(0, 10);
    const [bookingRows, customerRows, serviceRows, dayRows, staffRows, leaveRows] = await Promise.all([
      getAdminBookings(), getAdminCustomers(), getPublishedServices(), getAdminBookingDays(from, to), getAdminStaff(), getStaffTimeOff(),
    ]);
    const openDays = dayRows.filter((day) => day.is_open);
    setBookings(bookingRows);
    setCustomers(customerRows);
    setServices(serviceRows);
    setDays(openDays);
    setStaff(staffRows);
    setTimeOff(leaveRows);
    setCreateDate((current) => current || openDays[0]?.booking_date || '');
    const requested = params.get('booking');
    setSelectedId((current) => requested || current || bookingRows[0]?.id || '');
  }

  useEffect(() => {
    refresh()
      .catch((error) => setMessage(error instanceof Error ? error.message : 'Unable to load bookings.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setShowCreate(params.get('create') === '1');
  }, [params]);

  const customerFilter = params.get('customer');
  const visible = useMemo(() => bookings.filter((booking) => {
    const text = `${booking.reference} ${booking.service_name} ${booking.customers?.full_name || ''} ${booking.customers?.whatsapp || ''} ${booking.staff_members?.display_name || ''}`.toLowerCase();
    return text.includes(query.toLowerCase())
      && (statusFilter === 'all' || booking.status === statusFilter)
      && (!customerFilter || booking.customer_id === customerFilter);
  }), [bookings, query, statusFilter, customerFilter]);

  const selected = bookings.find((booking) => booking.id === selectedId) ?? visible[0];

  function staffReason(member: StaffMember, date: string, time: string) {
    if (!member.is_active) return 'Inactive';
    if (!date) return 'Choose a date';
    const day = new Date(`${date}T00:00:00Z`).getUTCDay();
    if (!member.working_days.includes(day)) return 'Not working this day';
    const start = toMinutes(time) ?? 0;
    const end = start + 60;
    const workStart = toMinutes(member.work_start) ?? 0;
    const workEnd = toMinutes(member.work_end) ?? 0;
    if (start < workStart || end > workEnd) return 'Outside working hours';
    const breakStart = toMinutes(member.break_start);
    const breakEnd = toMinutes(member.break_end);
    if (breakStart !== null && breakEnd !== null && start < breakEnd && end > breakStart) return 'On break';
    if (timeOff.some((item) => item.staff_id === member.id && date >= item.starts_on && date <= item.ends_on)) return 'On leave';
    return '';
  }

  function occupied(date: string, time: string, staffId: string, exceptId?: string) {
    const member = staff.find((item) => item.id === staffId);
    return bookings.some((booking) => booking.id !== exceptId
      && booking.appointment_date === date
      && booking.appointment_time?.slice(0, 5) === time
      && (booking.assigned_staff_id === staffId || (!booking.assigned_staff_id && member && booking.assigned_worker === member.slot_number))
      && activeStatuses.includes(booking.status));
  }

  function available(member: StaffMember, date: string, time: string, exceptId?: string) {
    return !staffReason(member, date, time) && !occupied(date, time, member.id, exceptId);
  }

  function nameFor(booking: AdminBooking) {
    return booking.staff_members?.display_name
      || staff.find((item) => item.id === booking.assigned_staff_id)?.display_name
      || (booking.assigned_worker ? `Staff position ${booking.assigned_worker}` : 'Pending');
  }

  useEffect(() => {
    if (!selected) return;
    const time = selected.appointment_time?.slice(0, 5) || '09:00';
    setScheduleTime(time);
    const current = selected.assigned_staff_id;
    const first = staff.find((item) => available(item, selected.appointment_date, time, selected.id));
    setScheduleStaffId(current || first?.id || '');
  }, [selected?.id, staff, timeOff]);

  useEffect(() => {
    if (!selected || !scheduleStaffId) return;
    const current = staff.find((item) => item.id === scheduleStaffId);
    if (!current || !available(current, selected.appointment_date, scheduleTime, selected.id)) {
      setScheduleStaffId(staff.find((item) => available(item, selected.appointment_date, scheduleTime, selected.id))?.id || '');
    }
  }, [scheduleTime]);

  useEffect(() => {
    const current = staff.find((item) => item.id === createStaffId);
    if (!current || !available(current, createDate, createTime)) {
      setCreateStaffId(staff.find((item) => available(item, createDate, createTime))?.id || '');
    }
  }, [createDate, createTime, staff, timeOff, bookings]);

  function openBooking(id: string) {
    setSelectedId(id);
    setParams({ booking: id });
    setShowCreate(false);
  }

  async function changeStatus(status: BookingStatus) {
    if (!selected) return;
    if (['approved', 'awaiting_payment', 'payment_pending', 'confirmed', 'arrived'].includes(status)
      && (!selected.appointment_time || !selected.assigned_staff_id)) {
      setMessage('Schedule the exact time and staff member before approving this booking.');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      const updated = await updateBookingStatus(selected.id, status);
      setBookings((current) => current.map((item) => item.id === updated.id
        ? { ...updated, customers: item.customers, staff_members: item.staff_members }
        : item));
      setMessage(`Booking changed to ${status.replaceAll('_', ' ')}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to update booking.');
    } finally {
      setSaving(false);
    }
  }

  async function saveSchedule() {
    if (!selected || !scheduleStaffId) {
      setMessage('Choose an available staff member.');
      return;
    }
    const member = staff.find((item) => item.id === scheduleStaffId);
    if (!member) return;
    const reason = staffReason(member, selected.appointment_date, scheduleTime);
    if (reason) {
      setMessage(`${member.display_name} is unavailable: ${reason}.`);
      return;
    }
    if (occupied(selected.appointment_date, scheduleTime, scheduleStaffId, selected.id)) {
      setMessage(`${member.display_name} is already booked at ${formatTime(scheduleTime)}.`);
      return;
    }
    setSaving(true);
    setMessage('Scheduling appointment…');
    try {
      const updated = await scheduleBooking(selected.id, scheduleTime, scheduleStaffId);
      setBookings((current) => current.map((item) => item.id === updated.id
        ? {
          ...updated,
          customers: item.customers,
          staff_members: { id: member.id, slot_number: member.slot_number, display_name: member.display_name, photo_url: member.photo_url, is_active: member.is_active },
        }
        : item));
      setMessage(`Scheduled for ${formatTime(updated.appointment_time)} with ${member.display_name}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to schedule booking.');
    } finally {
      setSaving(false);
    }
  }

  async function createBooking(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const member = staff.find((item) => item.id === createStaffId);
    if (!member) {
      setMessage('Choose an available staff member.');
      return;
    }
    const reason = staffReason(member, createDate, createTime);
    if (reason || occupied(createDate, createTime, member.id)) {
      setMessage(`${member.display_name} is unavailable at that date and time${reason ? `: ${reason}` : ''}.`);
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      let bookingId: string;
      let reference: string;
      const notes = String(data.get('notes') || 'Admin-created booking');
      const serviceSlug = String(data.get('service'));

      if (prefillCustomer) {
        const created = await createAdminBookingRequest({
          customerId: prefillCustomer.id,
          serviceSlug,
          appointmentDate: createDate,
          notes,
        });
        bookingId = created.id;
        reference = created.reference;
      } else {
        const created = await submitBookingRequest({
          serviceSlug,
          appointmentDate: createDate,
          fullName: String(data.get('name')),
          whatsapp: String(data.get('whatsapp')),
          email: String(data.get('email') || ''),
          preferredShape: String(data.get('shape') || ''),
          notes,
        });
        bookingId = created.booking_id;
        reference = created.reference;
      }

      await scheduleBooking(bookingId, createTime, member.id);
      await refresh();
      setShowCreate(false);
      setParams({ booking: bookingId });
      setSelectedId(bookingId);
      setMessage(`Booking ${reference} created and scheduled with ${member.display_name}${prefillCustomer ? ` for existing customer ${prefillCustomer.full_name}` : ''}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to create booking.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminLayout
      title="Bookings"
      description="Review date requests, reuse customer records, assign exact times and manage every appointment."
      actions={<button className="studio-admin-btn primary" onClick={() => { setShowCreate(true); setParams({ create: '1' }); }}>+ New booking</button>}
    >
      {message && <p className={/unable|already|before|unavailable|choose|outside|leave|break|not found/i.test(message) ? 'studio-admin-error' : 'studio-admin-feedback'} role="status">{message}</p>}

      {showCreate && (
        <form key={prefillCustomer?.id || 'new-customer'} className="studio-admin-card studio-admin-grid" onSubmit={createBooking}>
          <div>
            <p className="admin-eyebrow">NEW ADMIN BOOKING</p>
            <h2>{prefillCustomer ? `Book again for ${prefillCustomer.full_name}.` : 'Create and schedule an appointment.'}</h2>
            <p>{prefillCustomer
              ? 'This booking will reuse the selected customer record. Contact details are locked to prevent accidental duplication.'
              : 'New contact details are matched against existing customers before a record is created.'}</p>
          </div>
          {prefillCustomer && <p className="studio-admin-feedback" role="status">Existing customer recognized • {prefillCustomer.whatsapp} • {prefillCustomer.email || 'No email supplied'}</p>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 14 }}>
            <label className="studio-admin-field">Customer name<input name="name" required defaultValue={prefillCustomer?.full_name || ''} readOnly={Boolean(prefillCustomer)} /></label>
            <label className="studio-admin-field">WhatsApp<input name="whatsapp" required defaultValue={prefillCustomer?.whatsapp || ''} readOnly={Boolean(prefillCustomer)} /></label>
            <label className="studio-admin-field">Email<input name="email" type="email" defaultValue={prefillCustomer?.email || ''} readOnly={Boolean(prefillCustomer)} /></label>
            <label className="studio-admin-field">Shape<input name="shape" defaultValue={prefillCustomer?.preferred_shape || ''} readOnly={Boolean(prefillCustomer)} /></label>
            <label className="studio-admin-field">Service<select name="service" required>{services.map((service) => <option key={service.id} value={service.slug}>{service.name} — ₦{service.starting_price.toLocaleString('en-NG')}</option>)}</select></label>
            <label className="studio-admin-field">Date<select value={createDate} onChange={(event) => setCreateDate(event.target.value)} required>{days.map((day) => <option key={day.id} value={day.booking_date}>{formatDate(day.booking_date)}</option>)}</select></label>
            <label className="studio-admin-field">Time<select value={createTime} onChange={(event) => setCreateTime(event.target.value)} required>{timeOptions.map((time) => <option key={time} value={time}>{formatTime(time)}</option>)}</select></label>
            <label className="studio-admin-field">Staff member<select value={createStaffId} onChange={(event) => setCreateStaffId(event.target.value)} required><option value="" disabled>No staff available</option>{staff.map((member) => { const reason = staffReason(member, createDate, createTime) || (occupied(createDate, createTime, member.id) ? 'Already booked' : ''); return <option key={member.id} value={member.id} disabled={Boolean(reason)}>{member.display_name}{reason ? ` — ${reason}` : ''}</option>; })}</select></label>
            <label className="studio-admin-field" style={{ gridColumn: '1 / -1' }}>Booking notes<textarea name="notes" /></label>
          </div>
          <div className="studio-admin-actions">
            <button className="studio-admin-btn primary" type="submit" disabled={saving || !services.length || !days.length || !createStaffId}>{saving ? 'Creating…' : prefillCustomer ? 'Create for existing customer' : 'Create and schedule'}</button>
            <button className="studio-admin-btn" type="button" onClick={() => { setShowCreate(false); setParams({}); }}>Cancel</button>
          </div>
        </form>
      )}

      <section className="studio-admin-card studio-admin-grid">
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
          <label className="studio-admin-field">Search<input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Customer, staff, phone or reference" /></label>
          <label className="studio-admin-field">Status<select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="all">All statuses</option><option value="pending_approval">Pending approval</option><option value="approved">Approved</option><option value="awaiting_payment">Awaiting payment</option><option value="confirmed">Confirmed</option><option value="arrived">Arrived</option><option value="completed">Completed</option><option value="declined">Declined</option><option value="cancelled">Cancelled</option></select></label>
        </div>
        <div className="studio-admin-table">
          {loading ? <p>Loading bookings…</p> : visible.length ? visible.map((booking) => (
            <button key={booking.id} type="button" className={`studio-admin-row${booking.id === selected?.id ? ' selected' : ''}`} onClick={() => openBooking(booking.id)} style={{ textAlign: 'left', cursor: 'pointer' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr auto', gap: 14, alignItems: 'center' }}>
                <div><strong>{booking.customers?.full_name || 'Customer'}</strong><small style={{ display: 'block' }}>{booking.customers?.whatsapp} • {booking.reference}</small></div>
                <div><strong>{booking.service_name}</strong><small style={{ display: 'block' }}>₦{booking.service_price.toLocaleString('en-NG')}</small></div>
                <div><strong>{formatDate(booking.appointment_date)}</strong><small style={{ display: 'block' }}>{booking.appointment_time ? `${formatTime(booking.appointment_time)} • ${nameFor(booking)}` : 'Time and staff pending'}</small></div>
                <span className={`studio-admin-status ${booking.status}`}>{booking.status.replaceAll('_', ' ')}</span>
              </div>
            </button>
          )) : <p>No matching bookings.</p>}
        </div>
      </section>

      {selected && (
        <section className="studio-admin-card studio-admin-grid">
          <div><p className="admin-eyebrow">SELECTED BOOKING</p><h2>{selected.customers?.full_name} • {selected.service_name}</h2><p>{selected.reference} • Requested {formatDate(selected.appointment_date)}</p></div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 14 }}>
            <div><small>STATUS</small><p><span className={`studio-admin-status ${selected.status}`}>{selected.status.replaceAll('_', ' ')}</span></p></div>
            <div><small>APPOINTMENT TIME</small><p>{formatTime(selected.appointment_time)}</p></div>
            <div><small>ASSIGNED STAFF</small><p>{nameFor(selected)}</p></div>
            <div><small>WHATSAPP</small><p>{selected.customers?.whatsapp}</p></div>
            <div><small>EMAIL</small><p>{selected.customers?.email || 'Not supplied'}</p></div>
            <div><small>SHAPE</small><p>{selected.customers?.preferred_shape || 'Not supplied'}</p></div>
          </div>
          {!['declined', 'cancelled', 'completed'].includes(selected.status) && (
            <div className="studio-admin-card" style={{ background: '#f8f2ea' }}>
              <p className="admin-eyebrow">ADMIN SCHEDULING</p><h3>Assign the exact one-hour appointment.</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 14, alignItems: 'end' }}>
                <label className="studio-admin-field">Time<select value={scheduleTime} onChange={(event) => setScheduleTime(event.target.value)}>{timeOptions.map((time) => <option key={time} value={time}>{formatTime(time)}</option>)}</select></label>
                <label className="studio-admin-field">Staff member<select value={scheduleStaffId} onChange={(event) => setScheduleStaffId(event.target.value)}><option value="" disabled>No staff available</option>{staff.map((member) => { const reason = staffReason(member, selected.appointment_date, scheduleTime) || (occupied(selected.appointment_date, scheduleTime, member.id, selected.id) ? 'Already booked' : ''); return <option key={member.id} value={member.id} disabled={Boolean(reason)}>{member.display_name}{reason ? ` — ${reason}` : ''}</option>; })}</select></label>
                <button className="studio-admin-btn primary" type="button" onClick={saveSchedule} disabled={saving || !scheduleStaffId}>{selected.appointment_time ? 'Reschedule' : 'Save schedule'}</button>
              </div>
              <p>Business hours and each staff member’s personal schedule are enforced by the database.</p>
            </div>
          )}
          <div><small>CUSTOMER NOTES</small><p>{selected.customer_notes || selected.customers?.notes || 'No notes supplied.'}</p></div>
          <div className="studio-admin-actions">
            <button className="studio-admin-btn primary" disabled={saving || !selected.appointment_time} onClick={() => changeStatus('approved')}>Approve</button>
            <button className="studio-admin-btn" disabled={saving || !selected.appointment_time} onClick={() => changeStatus('awaiting_payment')}>Ready for Monnify</button>
            <button className="studio-admin-btn" disabled={saving || !selected.appointment_time} onClick={() => changeStatus('confirmed')}>Confirm manual payment</button>
            <button className="studio-admin-btn" disabled={saving || !selected.appointment_time} onClick={() => changeStatus('arrived')}>Mark arrived</button>
            <button className="studio-admin-btn" disabled={saving} onClick={() => changeStatus('completed')}>Complete</button>
            <button className="studio-admin-btn danger" disabled={saving} onClick={() => changeStatus('declined')}>Decline</button>
            <button className="studio-admin-btn danger" disabled={saving} onClick={() => changeStatus('cancelled')}>Cancel</button>
          </div>
          <p><strong>Payment safeguard:</strong> scheduling and approval do not claim that payment was received. Manual confirmation must be used only after independent verification.</p>
        </section>
      )}
    </AdminLayout>
  );
}
